<template>
  <component
    :is="props.clickable ? RevButtonCard : RevCard"
    class="!bg-static-info-mid flex h-[100px] w-full flex-row items-center gap-12"
    @click="handleClick"
  >
    <!-- eslint-disable tailwindcss/no-custom-classname -->
    <RevIllustration
      v-if="dynamicImage"
      alt=""
      aria-hidden="true"
      class="rounded-l-lg h-full"
      :class="[gradeImage ? `grade-gradient-${grade}` : '']"
      :height="100"
      :src="dynamicImage"
      :width="100"
    />
    <!-- eslint-enable tailwindcss/no-custom-classname -->

    <div class="flex flex-1 items-center gap-12 py-12 pr-16">
      <div class="flex-1 text-left">
        <span v-if="isConditionModal" class="body-2-bold block">
          {{ gradeDisplayName }}
        </span>
        <span v-if="!isConditionModal" class="body-2-bold block">
          {{
            typeof label === 'object' ? i18n(label as I18nDefinition) : label
          }}
        </span>
        <span v-if="isConditionModal" class="caption mt-8 line-clamp-3">
          <div v-if="!isNewDescriptionActive || hasScreenAndBody">
            {{ i18n(translations.screen) }} :
            {{ dynamicScreenGradeDescription }}
          </div>
          <div v-if="isPremiumSelected && isNewDescriptionActive">
            {{ i18n(translations.battery) }} :
            {{ i18n(translations.batteryDescription9New) }}
          </div>
          <div v-if="isPremiumSelected && !isNewDescriptionActive">
            {{ i18n(translations.battery) }} :
            {{ i18n(translations.batteryDescription9) }}
          </div>
          <div>
            {{ i18n(translations.body) }} :
            {{ dynamicBodyGradeDescription }}
          </div>
        </span>

        <span
          v-if="description && !isConditionModal"
          class="caption mt-8 line-clamp-3"
        >
          {{ description }}
        </span>
      </div>

      <IconChevronRight v-if="!isLink" />
      <IconExternalLink v-if="isLink" />
    </div>
  </component>
</template>

<script lang="ts" setup>
import { type Component, computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonCard } from '@ds/components/ButtonCard'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { openModal } from '@ds/components/ModalBase'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconExternalLink } from '@ds/icons/IconExternalLink'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

import { getGradeImage } from '../../../../utils/getGradeImage'
import type { Step } from '../../utils/types'

import translations from './CardGuidance.translations'

const props = defineProps<{
  modalName: string
  icon?: string | Component
  image?: string
  label: string
  description?: string
  clickable?: boolean
  tracking?: { zone: string; name: string }
  step?: Step
}>()

const track = useTracking()
const i18n = useI18n()

const isLink = computed(() => {
  return props.clickable && props.modalName === ''
})

const { grade } = useUrlParams()

const isConditionModal = computed(() => props.modalName === 'CONDITION')

const experiments = useExperiments()
const isNewDescriptionActive =
  experiments['experiment.ppJapanNewDescription'] === 'withNewDescription'

const dynamicBodyGradeDescription = computed(() => {
  if (!grade.value) return ''

  // Dynamically construct the key name
  const translationKey = isNewDescriptionActive
    ? (`bodyDescription${grade.value}New` as keyof typeof translations)
    : (`bodyDescription${grade.value}` as keyof typeof translations)

  const translation = translations[translationKey]

  return translation ? i18n(translation) : grade.value.toString()
})

const dynamicScreenGradeDescription = computed(() => {
  if (!grade.value) return ''

  // Dynamically construct the key name
  const translationKey = isNewDescriptionActive
    ? (`screenDescription${grade.value}New` as keyof typeof translations)
    : (`screenDescription${grade.value}` as keyof typeof translations)

  const translation = translations[translationKey]

  return translation ? i18n(translation) : grade.value.toString()
})

const gradeDisplayName = computed(() => {
  if (!grade.value) return ''

  // Dynamically construct the key name
  const translationKey = `grade${grade.value}` as keyof typeof translations
  const translation = translations[translationKey]

  return translation ? i18n(translation) : grade.value.toString()
})

const isPremiumSelected = computed(() => {
  return grade.value === 9
})

const hasScreenAndBody = computed(() => {
  if (!props.step?.sideContent?.content) return false

  return (props.step.sideContent.content[0]?.items?.length ?? 0) > 1
})

function openDaModal(event?: MouseEvent) {
  openModal(props.modalName, event)
}

function handleClick(event: MouseEvent) {
  if (!isLink.value) {
    openDaModal(event)
  } else {
    window.open(
      'https://www.backmarket.co.jp/ja-jp/service/rakuten-mobile',
      '_blank',
    )
  }

  if (props.tracking)
    track.trackClick({
      name: props.tracking.name,
      zone: props.tracking.zone,
    })
}

const gradeImage = computed(() => {
  if (props.step?.id === 'grades') {
    return getGradeImage(props.step, grade.value, 'Body')
  }

  return undefined
})

const dynamicImage = computed(() => {
  return gradeImage.value || props.image
})
</script>

<style scoped>
/* Grade gradient background */
/* PREMIUM */
:deep(.grade-gradient-9) {
  background:
    linear-gradient(236deg, rgba(0, 0, 0, 0) 11.69%, #000 91.87%), #686a6e;
}
/* EXCELLENT */
:deep(.grade-gradient-10) {
  background:
    linear-gradient(146deg, rgba(143, 162, 223, 0) 7.32%, #cce3ff 87.5%),
    linear-gradient(180deg, #8fa2de 0%, #c4b8f7 100%);
}
/* GOOD */
:deep(.grade-gradient-11) {
  background:
    linear-gradient(146deg, rgba(162, 105, 232, 0) 7.32%, #ffccfd 87.5%),
    linear-gradient(180deg, #a269e8 0%, #e2b8f7 100%);
}
/* FAIR */
:deep(.grade-gradient-12) {
  background:
    linear-gradient(
      145deg,
      rgba(208, 132, 195, 0) 0.71%,
      rgba(232, 222, 197, 0.9) 86.15%
    ),
    linear-gradient(180deg, #d084c4 0%, #e1b2de 100%);
}
</style>
